// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:locale\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/account/delete\",\"regex\":\"^/([^/]+)/account/delete$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/auth/success\",\"regex\":\"^/([^/]+)/auth/success$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/bonuses\",\"regex\":\"^/([^/]+)/casino/bonuses$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/bonuses/cashback\",\"regex\":\"^/([^/]+)/casino/bonuses/cashback$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/bonuses/cryptobonus\",\"regex\":\"^/([^/]+)/casino/bonuses/cryptobonus$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/bonuses/vip\",\"regex\":\"^/([^/]+)/casino/bonuses/vip$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/categories/:category\",\"regex\":\"^/([^/]+)/casino/categories/([^/]+)$\",\"paramNames\":[\"locale\",\"category\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/categories/bonus-games\",\"regex\":\"^/([^/]+)/casino/categories/bonus-games$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/favourites\",\"regex\":\"^/([^/]+)/casino/favourites$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/providers\",\"regex\":\"^/([^/]+)/casino/providers$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/casino/providers/:provider\",\"regex\":\"^/([^/]+)/casino/providers/([^/]+)$\",\"paramNames\":[\"locale\",\"provider\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/docs\",\"regex\":\"^/([^/]+)/docs$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/docs/:docType\",\"regex\":\"^/([^/]+)/docs/([^/]+)$\",\"paramNames\":[\"locale\",\"docType\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/games/:game\",\"regex\":\"^/([^/]+)/games/([^/]+)$\",\"paramNames\":[\"locale\",\"game\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/banned\",\"regex\":\"^/([^/]+)/banned$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/mp-redirect\",\"regex\":\"^/([^/]+)/mp-redirect$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/soon\",\"regex\":\"^/([^/]+)/soon$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true}],\"staticRoutes\":[],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.0";import * as Sentry from "@sentry/nextjs";
import {captureOwnerStack} from "react";
import {LOCALE_SOURCE_ATTRIBUTE} from "@/lib/utils/contants/localeSource";

/**
 * Session Replay sampling (AC-803).
 *
 * Replay (`rrweb` + `@sentry-internal/replay` + the replay worker) is ~120 KB of
 * the chunk every visitor downloads before first paint, while only 10% of
 * sessions are actually recorded. So the integration is no longer part of the
 * eager `integrations` array — it is attached after `init`, and only for the
 * sessions that will use it.
 *
 * The sample decision is rolled ONCE, here, and handed to the SDK as a 1/0 rate.
 * Letting the integration sample itself as well would compound the two rolls and
 * silently turn 10% into 1%.
 *
 * Two paths, matching the two rates this project has always used:
 *  - Sampled session (10%) — load immediately; `replaysSessionSampleRate: 1`
 *    makes the integration start recording the whole session as it did before.
 *  - Everyone else — load on the first error instead of on page load, and let
 *    `replaysOnErrorSampleRate` start it in buffer mode. The trade-off is
 *    deliberate: the buffer begins at that first error rather than 60s before
 *    it, so the error that triggered the load has no replay of its own and a
 *    subsequent one does.
 */
const REPLAY_SESSION_SAMPLE_RATE = 0.1;
const REPLAY_ON_ERROR_SAMPLE_RATE = 1.0;

const isReplaySessionSampled = Math.random() < REPLAY_SESSION_SAMPLE_RATE;

/**
 * ALDEX-CASINO-8V / -8B — see the `beforeSend` block below for the reasoning.
 *
 * The root exception comes last in linked exceptions, the same one `denyUrls`
 * picks; an aggregate's children carry `mechanism.parent_id` and are skipped.
 */
const isInjectedExitPictureInPictureRejection = (
  event: Sentry.ErrorEvent,
): boolean => {
  const rootException = [...(event.exception?.values ?? [])]
    .reverse()
    .find((value) => value.mechanism?.parent_id === undefined);

  if (rootException?.type !== "InvalidStateError") {
    return false;
  }

  return (
    rootException.stacktrace?.frames?.some(
      (frame) => frame.function === "exitPictureInPicture",
    ) ?? false
  );
};

/**
 * ALDEX-CASINO-6F (AC-886) — React #185 ("Maximum update depth exceeded")
 * escaping as an unhandled rejection. React throws it from whichever
 * `setState` happens to run after the nested-update counter is already
 * blown; on `/:locale` that is `next/image`'s `setBlurComplete` inside an
 * un-awaited `img.decode()` continuation. So the trace is vendor-only, names
 * no looping component, and `app/[locale]/error.tsx` never sees it.
 *
 * Attribution only, no behavioural change: tag the route the loop happened
 * on so the event can be filtered and lined up with its replay, and in dev
 * attach React's owner stack. `captureOwnerStack` is exported by React's
 * development build only, hence the `NODE_ENV` gate.
 */
const REACT_UPDATE_DEPTH_ERROR =
  /Maximum update depth exceeded|Minified React error #185/;

const tagUnhandledUpdateDepthRejection = (event: Sentry.ErrorEvent): void => {
  const isUpdateDepthRejection =
    event.exception?.values?.some(
      (value) =>
        value.mechanism?.type ===
          "auto.browser.global_handlers.onunhandledrejection" &&
        REACT_UPDATE_DEPTH_ERROR.test(value.value ?? ""),
    ) ?? false;

  if (!isUpdateDepthRejection) {
    return;
  }

  event.tags = {
    ...event.tags,
    react_update_depth_route: window.location.pathname,
  };

  if (process.env.NODE_ENV !== "production") {
    const ownerStack = captureOwnerStack();
    if (ownerStack) {
      event.extra = {...event.extra, react_owner_stack: ownerStack};
    }
  }
};

Sentry.init({
  dsn: "https://8254ac30b86784d65d9d23574bdf8e23@o4511180749602816.ingest.us.sentry.io/4511180786040832",

  // Drop noise from third-party WebView autofill / injected scripts
  // (e.g. `ReferenceError: xbrowser is not defined` thrown from an
  // injected `execute_auto_fill` routine on Android Chrome WebView).
  // These run outside our app and are not actionable — see ALDEX-CASINO-14.
  //
  // `M_ID` — ALDEX-CASINO-7X: an unhandled rejection thrown from
  // `executors/200.js`, which we do not serve (that path 404s on the site) and
  // which carries no sourcemap, so both frames stay minified. It only *looks*
  // first-party because `experimentalThirdPartyOriginStackFrames` is off, which
  // makes the Next.js rewriteFrames iteratee rewrite every origin — third-party
  // included — to `app:///`. Matched loosely on the property name because the
  // surrounding phrasing differs per engine ("Cannot read properties of
  // undefined (reading 'M_ID')" vs. Safari's "undefined is not an object"), and
  // `M_ID` appears nowhere in our own source.
  //
  // `InboundFilters` — ALDEX-CASINO-7R: same story from the Zendesk widget
  // (`static.zdassets.com/ekr/snippet.js`, mounted by `ZEInitializer`). Their
  // `asset-composer` build lazy-loads its own bundled Sentry SDK and calls
  // `new Integrations.InboundFilters` on it — a Sentry v7 export that no longer
  // exists in the v8+ chunk their loader resolves — inside an uncaught promise
  // chain, so it reaches us only as an unhandled rejection. Nothing to catch on
  // our side, and it is their error telemetry failing, not the support widget.
  // `InboundFilters` is a v7 name; we are on v10, so it cannot match our own.
  //
  // `HTMLIFrameElement object could not be cloned` — ALDEX-CASINO-8A: a
  // `DataCloneError` from an injected media-filtering agent
  // (`src/mediafilter.generic-wrapper.min.js`, another `app:///` rewrite
  // false positive) that replaces the `HTMLIFrameElement.prototype
  // .contentDocument` accessor with one that `postMessage`s the iframe element
  // itself — never structured-cloneable, so the getter throws on every read.
  // Session Replay's rrweb reads that getter unguarded while serializing
  // mutations, inside its own `MutationObserver` callback, so the throw reaches
  // `window.onerror` with nothing of ours in between. Matched on the distinctive
  // substring rather than the `DataCloneError` name, which would also swallow
  // genuine structured-clone bugs in our Web Push / service-worker messaging.
  // This silences the report only; the recording gap is in vendor code.
  //
  // `TypeError: Permissions check failed` — ALDEX-CASINO-6P: Chromium's own
  // rejection string for a Fullscreen API call denied by Permissions Policy
  // (MDN documents `requestFullscreen()` as the one API that rejects with a
  // TypeError, not a DOMException, on a policy denial). Not ours: no Fullscreen
  // API appears anywhere in first-party source — the game page's three-stage
  // fullscreen control (`FullScreenModesEnum`) is CSS only (`fixed inset-0
  // z-50` / `h-dvh`) and never calls `requestFullscreen()`. The call comes from
  // the provider game frame injected by the third-party `GameLauncher`
  // connector, or from an injected browser agent of the -7X / -7R / -8A / -8P /
  // -8W / -8V family. Neither of the other two filters can reach it: the event
  // carries ZERO stack frames (`stacktrace: null`), so `denyUrls` (which keys
  // on a frame filename) and the `beforeSend` frame-function approach used for
  // -8V below would both match nothing and silently leave the issue open.
  //
  // Anchored on the `type: value` candidate on purpose. Chromium reuses the
  // phrase "Permissions check failed" for other permissions-policy-gated APIs
  // as a `NotAllowedError` — including the Clipboard API, which we do call in
  // eight places (`CopyBtn`, `CryptoDepositForm`, `H2H`,
  // `AvatarPopoverContent`, `mp-redirect`, …). A bare `/Permissions check
  // failed/` entry would swallow a genuine clipboard regression; anchoring to
  // `TypeError:` cannot, because the clipboard form carries a different
  // exception type. Unlike the `/InboundFilters/` entry above (AC-818 —
  // regressed because it keyed on engine-dependent phrasing), this string is
  // emitted by Chromium itself and is identical across Chrome versions; other
  // engines word their fullscreen policy rejection differently, so a
  // reappearance from one of them groups separately and stays visible instead
  // of being silently swallowed. This silences the report only; there is no
  // first-party behaviour to fix.
  //
  // `setDgResult` — ALDEX-CASINO-9A: `TypeError: window.setDgResult is not a
  // function` from an Android Chrome WebView (in-app browser). The only frame
  // is `<anonymous>:1:8` — a one-line script the host app pushes into the page
  // via `evaluateJavascript`, calling a callback it expects its own bridge to
  // have installed. `setDgResult` appears nowhere in our source or in any
  // shipped dependency. Matched on the identifier rather than the full
  // message, whose "is not a function" phrasing differs per engine (Safari
  // appends `(In 'window.setDgResult(…)'…)`). This silences the report only;
  // there is nothing of ours to fix.
  //
  // `AbortError: signal is aborted without reason` — ALDEX-CASINO-8Y (AC-887):
  // `LogOutForm` races the web-push teardown (`DELETE /api/v1/push-tokens` on
  // the auth API) against a 2 s timeout and, when the timeout wins, abandons
  // the still-running mutation on purpose so logout never freezes (AC-649).
  // The `authApi.util.resetApiState()` that follows then aborts every running
  // mutation with no reason, so the signal's reason is the native DOMException
  // — rejected unhandled by a signal consumer outside our code (browser fetch /
  // body stream or Sentry's fetch instrumentation), since our `unwrap()` chain
  // is caught. The cancellation is intended and the token is cleaned up on the
  // next send. Anchored on the exact `type: value` candidate so it cannot hide
  // an AbortError that carries a reason or a message of our own. The filter is
  // still global — `ignoreErrors` sees only the message, not the call site — so
  // it also covers `DeleteAccountForm`, which runs the same three
  // `resetApiState()` calls; a new `resetApiState()` caller that cancels a
  // request it should not would be silenced too. This silences the report only.
  //
  // `domInteractive` — ALDEX-CASINO-9F: `TypeError: Cannot read properties of
  // undefined (reading 'domInteractive')` from the TikTok in-app browser
  // (Android WebView). Same shape as -9A above: the only frames are
  // `<anonymous>:1` (`e.checkPerfReady`), a script the host app injects to
  // poll page-load timing, which reads a navigation-timing entry that does not
  // exist yet, run from a `setTimeout` Sentry wraps. `checkPerfReady` appears
  // nowhere in our source, and the one shipped dependency that touches
  // `domInteractive` — Sentry Replay — destructures it from an entry the
  // `PerformanceObserver` hands it, which cannot yield this message. Anchored
  // on Chromium's `(reading '…')` phrasing, since the injector ships in an
  // Android WebView; a Safari-worded reappearance groups separately and stays
  // visible. This silences the report only; there is nothing of ours to fix.
  //
  // `UnknownError: …` — ALDEX-CASINO-7C / -7V / -7K (AC-893): an IndexedDB
  // `DOMException` (`code: 0`) rejected unhandled after WebKit dropped the
  // origin's IndexedDB mid-session, all from iOS 26 in-app WebViews (Google
  // app, Facebook app) — the affiliate cohort. The three groups are WebKit's
  // three phrasings of it: "An internal error was encountered in the Indexed
  // Database server", "Connection to Indexed Database server lost…" and
  // "Database deleted by request of the user". The IndexedDB is Mixpanel's
  // session recorder's, not Firebase/FCM's: the session's replay logs
  // `Mixpanel error: [recorder] IndexedDB error:` (`RecordingRegistry`, which
  // exists only in the recorder bundle) and `[batch] Error retrieving queue`
  // in the same millisecond window, with identical message text. Our own
  // source never touches IndexedDB. The event carries ZERO frames — Safari
  // attaches no stack to a DOMException — so `denyUrls` and the `beforeSend`
  // frame approach below would match nothing; the message is all there is.
  //
  // Keyed on the DOMException name AND WebKit's IndexedDB wording, not on the
  // bare name: `UnknownError` is also raised by other APIs (WebAuthn among
  // them) and by whatever third-party script shares the page, and a bare
  // `/^UnknownError: /` would silently drop all of those. The two substrings
  // cover the three observed messages and survive most rewordings of WebKit's
  // IndexedDB-server messages, which name "Indexed Database"; an
  // `UnknownError` that is not about IndexedDB stays visible. If WebKit ever phrases it without either substring, that
  // surfaces as a new group — loud, not silent — and gets its own entry.
  // Message-based, so it also covers any IndexedDB failure of Firebase/FCM
  // (best-effort by construction) and of any IndexedDB code we add later —
  // revisit this entry if we do. This silences the report only; the
  // unattached promise is inside the vendor bundle.
  ignoreErrors: [
    /xbrowser is not defined/,
    "execute_auto_fill",
    /M_ID/,
    /InboundFilters/,
    /HTMLIFrameElement object could not be cloned/,
    /^TypeError: Permissions check failed$/,
    /setDgResult/,
    /^AbortError: signal is aborted without reason$/,
    /Cannot read properties of undefined \(reading 'domInteractive'\)/,
    /^UnknownError: .*(Indexed Database|Database deleted)/,
  ],

  // Matched against a stack-frame filename rather than the message: `denyUrls`
  // tests the topmost frame of the event's root exception.
  //
  // ALDEX-CASINO-7R — the Zendesk widget loader
  // (`static.zdassets.com/ekr/snippet.js`, mounted by `ZEInitializer`) described
  // under `InboundFilters` above. Its rejection message is engine-dependent:
  // current Firefox reports `can't access property "InboundFilters", i is
  // undefined`, while Firefox 115 ESR reports the terse `i is undefined`, which
  // the `/InboundFilters/` entry does not match — so the issue kept regressing
  // on the very release that filtered it. The one frame is the only stable part
  // of the event, so key on that instead.
  //
  // Substring match, never anchored. The filter integration is first in the
  // browser SDK's default integrations and `NextjsClientStackFrameNormalization`
  // is appended after it, and event processors run in registration order — so
  // filtering happens BEFORE the rewrite that turns origins into `app:///`, and
  // the string tested here is still the raw
  // `https://static.zdassets.com/ekr/snippet.js?key=…`. Anchoring this on
  // `app:///` would therefore match nothing and silently reopen the issue;
  // unanchored covers the raw URL and the rewritten form both. Neither form can
  // collide with our own frames, which carry `/_next/…`.
  //
  // ALDEX-CASINO-8P / -8A — the injected media-filtering agent described under
  // `HTMLIFrameElement object could not be cloned` above. Its `DataCloneError`
  // names whatever object it happened to hand `postMessage` on that code path,
  // so a message-shaped entry only ever covers one payload class: the 8A entry
  // shipped and 8P still reported, because the payload had become a
  // `PerformanceServerTiming` read off `performance.getEntriesByType`. Keying
  // on the frame is payload-agnostic and survives the next class the agent
  // picks. `src/setup.js:27` is the agent's own `postMessage` call site and the
  // last named frame on every event of both issues — the two events that carry
  // a trailing `<anonymous> (postMessage)` frame land here too, since the
  // lookup skips `<anonymous>` and `[native code]`. We ship no `src/`
  // directory, so this cannot match our own code.
  //
  // ALDEX-CASINO-8W / -8T — `TypeError: Failed to fetch
  // (www.google-analytics.com)`, an unhandled rejection out of Google's
  // gtag.js measurement transport. gtag is ours (`<GoogleAnalytics>` in the
  // dashboard layout) and flushes its beacon through `window.fetch`, but a
  // third-party injected script monkey-patches `window.fetch` ahead of us and
  // refuses the request: the last frame is `injectScriptAdjust.js` (obfuscated
  // identifiers, a `…window.fetch` override — the signature of a
  // content-blocking extension), another `app:///` rewrite false positive.
  // gtag attaches no rejection handler on that path, so there is nothing of
  // ours to catch it. Keyed on the frame, not the message: the message names
  // whichever host the agent blocked that time, and a `/Failed to fetch/`
  // entry would also swallow genuine `baseQueryWithRefresh` network failures
  // against the auth / games / wallet APIs. Both groups are the same browsing
  // session and share this last frame. This silences the report only — the
  // dropped beacon is the visitor's blocker doing its job.
  //
  // ALDEX-CASINO-8Z — `TypeError: Failed to fetch (cxg.playwebservice.com)`,
  // the same shape against our own games API: an extension content script
  // (`scripts/content/activeContent.js`) patches `window.fetch` ahead of
  // Sentry and forks an unhandled promise inside its wrapper. The request
  // itself is ours and already handled — `fetchBaseQuery` catches the
  // rejection and `baseQueryWithRefresh` returns a `FETCH_ERROR` the game page
  // renders — so only the extension's leaked copy reaches the global handler.
  // Keyed on the frame for the same reason as the entry above; we ship no
  // `scripts/content/` path, so this cannot match our own code.
  denyUrls: [
    /ekr\/snippet\.js/,
    /\/src\/setup\.js/,
    /injectScriptAdjust\.js/,
    /scripts\/content\/activeContent\.js/,
  ],

  // ALDEX-CASINO-8V / -8B — `InvalidStateError: The object is in an invalid
  // state.` (`DOMException.code: 11`), an unhandled rejection from
  // `document.exitPictureInPicture()` called while nothing was in
  // Picture-in-Picture. The call site is not ours: no Picture-in-Picture API
  // appears in first-party source or in any shipped dependency, and the app
  // renders no `<video>` or `<audio>` element at all. It comes from an agent
  // injected into the visitor's iOS 26 browser, and Safari attributes a throw
  // it cannot attribute to the document's own "global code" — so the only
  // named frame is `app:///es-ar`, another `app:///` rewrite false positive of
  // the same family as -7X / -7R / -8A / -8P / -8W above.
  //
  // Neither filter above can express this one:
  //  - `ignoreErrors` only ever sees `event.message` and the root exception's
  //    `value` / `type: value` (`getPossibleEventMessages`), never a frame
  //    function name — so an `/exitPictureInPicture/` entry would match
  //    nothing and silently leave the issue open. Keying on the message
  //    instead is far too broad: `InvalidStateError` is a genuine failure mode
  //    for IndexedDB (which Mixpanel and Firebase/FCM use — the Mixpanel
  //    recorder is what failed in ALDEX-CASINO-7C / -7V / -7K, see
  //    `UnknownError` above), for XHR, for service-worker registration and
  //    for WebSocket.
  //  - `denyUrls` walks the frames backwards past `[native code]`
  //    (`_getLastValidUrl`) and lands on `app:///es-ar` — our own document.
  //    An entry for that would deny-list the site's own HTML and drop every
  //    genuine error whose top frame is the document.
  //
  // So key on the native API name instead, which is message-agnostic (it
  // survives the per-locale group fragmentation that split -8V from -8B, and
  // any rewording of the DOMException text) and cannot collide with
  // first-party code. This silences the report only; the stray call lives in
  // the visitor's browser and is not ours to fix.
  //
  // ALDEX-CASINO-6F — see `tagUnhandledUpdateDepthRejection` above.
  beforeSend: (event) => {
    if (isInjectedExitPictureInPictureRejection(event)) {
      return null;
    }

    tagUnhandledUpdateDepthRejection(event);
    return event;
  },

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 1,
  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Already sampled above — 1 or 0 for this session, never a rate the
  // integration would roll a second time. Effective session rate stays 10%.
  replaysSessionSampleRate: isReplaySessionSampled ? 1.0 : 0,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE,

  // Enable sending user PII (Personally Identifiable Information)
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
});

// AC-655 / ALDEX-CASINO-9 — which signal the server resolved this page's locale
// from (`prefix` / `cookie` / `header` / `guess`, see `proxy.ts`). Read from the
// attribute the root layout stamped on `<html>`, so a Spanish-locale device served
// `lang="en"` says whether a stale cookie, an English-first `Accept-Language` or a
// header-less guess put it there.
const localeSource = document.documentElement.getAttribute(LOCALE_SOURCE_ATTRIBUTE);
if (localeSource) {
  Sentry.setTag("locale_source", localeSource);
}

/** Deduplicates the import across the two paths below and across repeated errors. */
let replayAttach: Promise<void> | null = null;

const attachReplay = (): Promise<void> => {
  if (!replayAttach) {
    replayAttach = import("@sentry/nextjs")
      .then(({replayIntegration}) => {
        Sentry.addIntegration(replayIntegration());
      })
      .catch((error: unknown) => {
        // Replay is diagnostics, never a reason to break the page.
        console.warn("[sentry] session replay could not be loaded", error);
      });
  }

  return replayAttach;
};

if (isReplaySessionSampled) {
  void attachReplay();
} else {
  Sentry.getClient()?.on("beforeSendEvent", (event) => {
    // Error events only — `event.type` is set for transactions, logs and the
    // replay envelopes themselves.
    if (!event.type && event.exception) {
      void attachReplay();
    }
  });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
